import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Layout,
  Padding,
  Text,
} from "@kidslivesafe/sc-react";
import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../../hooks/hooks";
import {
  selectActivatedFromDashboard,
  selectProtectionStatus,
  setProfileIdentity,
} from "../../../features/profileProtection/profileProtectionSlice";
import { selectMemberData } from "../../../features/authentication/authenticationSlice";
import { sendMemberEventAsync } from "../../../features/admin/adminSlice";
import type { PeopleReport } from "../../../features/reports/reportTypes";

interface ReportProtectionWidgetProps {
  visible?: boolean;
  report?: PeopleReport;
}

const ReportProtectionWidget: React.FC<ReportProtectionWidgetProps> = ({
  visible = true,
  report,
}) => {
  const dispatch = useAppDispatch();
  const handleNavigate = useHandlePageNavigate();
  const protectionStatus = useAppSelector(selectProtectionStatus);
  const activatedFromDashboard = useAppSelector(selectActivatedFromDashboard);
  const memberData = useAppSelector(selectMemberData);
  const [isDismissed, setIsDismissed] = useState(false);

  const inProgress =
    protectionStatus === "in_progress" || protectionStatus === "completed";

  const showsPrompt = visible && !isDismissed && !inProgress;

  // Fire an impression every time the "Is this you?" prompt becomes visible
  // on a People Report. Each report view is its own decision moment, so we
  // intentionally do NOT gate this once-per-session.
  useEffect(() => {
    if (!showsPrompt) {
      return;
    }
    dispatch(
      sendMemberEventAsync({
        EventCategory: activatedFromDashboard ? "Dashboard" : "People Report",
        EventAction: "Protection Flow Displayed",
        MetaData: JSON.stringify({
          reportFirstName: report?.firstName ?? "",
          reportLastName: report?.lastName ?? "",
          accountFirstName: memberData?.firstname ?? "",
          accountLastName: memberData?.lastname ?? "",
        }),
      }),
    );
    // Fires whenever the prompt-visible state flips true, or when the
    // underlying report token changes (different report → new impression).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showsPrompt, report?.reportToken]);

  if (!visible || isDismissed) {
    return null;
  }

  const reportName = `${report?.firstName || ""} ${
    report?.lastName || ""
  }`.trim();
  const reportLocation =
    report?.addresses?.[0]?.city && report?.addresses?.[0]?.state
      ? `${report.addresses[0].city}, ${report.addresses[0].state}`
      : report?.addresses?.[0]?.city || report?.addresses?.[0]?.state || "";

  const parsedAge = report?.age ? Number.parseInt(report.age, 10) : undefined;
  const age = Number.isNaN(parsedAge) ? undefined : parsedAge;
  const knownLocations = (report?.addresses || [])
    .map(address =>
      [
        address.fullAddress,
        [
          [address.city, address.state].filter(Boolean).join(", "),
          address.postalCode ? address.postalCode.split("-")[0] : "",
        ]
          .filter(Boolean)
          .join(" "),
      ]
        .filter(Boolean)
        .join(" - "),
    )
    .filter(Boolean);
  const relatives = (report?.relatives || [])
    .map(relative =>
      [relative.firstName, relative.middleName, relative.lastName]
        .filter(Boolean)
        .join(" ")
        .trim(),
    )
    .filter(Boolean);

  const sendMemberEvent = () => {
    dispatch(
      sendMemberEventAsync({
        EventCategory: activatedFromDashboard ? "Dashboard" : "People Report",
        EventAction: "Protection Flow Started",
      }),
    );
  };

  return (
    <Container
      containerTitle="Profile Protection"
      containerId="people-report__protection-widget"
    >
      <Padding space="md">
        <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
          {!inProgress ? (
            <>
              <Layout
                type="flex"
                flexProps={{ gap: "xs", alignItems: "center" }}
              >
                <Text fontWeight="bold">Is this you?</Text>
                <Text size="xs" color="accent">
                  Included
                </Text>
              </Layout>

              <Text size="sm">
                If this is your profile, we can hide your info from the view of
                others and request removal from other major sites where it may
                be exposed.
              </Text>
              <Layout
                type="flex"
                flexProps={{ justifyContent: "flex-start", gap: "sm" }}
              >
                <Button
                  buttonText="Start removal"
                  color="primary"
                  handleClick={() => {
                    sendMemberEvent();
                    dispatch(
                      setProfileIdentity({
                        name: reportName,
                        location: reportLocation,
                        reportToken: report?.reportToken || "",
                        reportVersion: report?.reportSummary?.version,
                        age,
                        knownLocations,
                        relatives,
                      }),
                    );
                    handleNavigate("/protection/scan");
                  }}
                />
                <Button
                  buttonText="Not me"
                  color="secondary"
                  handleClick={() => setIsDismissed(true)}
                />
              </Layout>
            </>
          ) : (
            <>
              <Text fontWeight="bold">Protection in progress</Text>
              <Text size="sm">
                Your removals are active. Continue in Status Hub to monitor
                progress.
              </Text>
              <Layout type="flex" flexProps={{ justifyContent: "flex-start" }}>
                <Button
                  buttonText="View status"
                  color="secondary"
                  handleClick={() => handleNavigate("/protection/status")}
                />
              </Layout>
            </>
          )}
        </Layout>
      </Padding>
    </Container>
  );
};

export default ReportProtectionWidget;
